import { Card } from "@/src/components/wrappers/Card";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { AddBookingPayment } from "../../AddBookingPayment/AddBookingPayment";
import { EditBookingPayment } from "../../EditBookingPayment/EditBookingPayment";
import RemoveBookingPayment from "../../RemoveBookingPayment/RemoveBookingPayment";
import BookingPaymentLogs from "../../BookingPaymentLogs/BookingPaymentLogs";
import { PaymentEmailDialog } from "../../PaymentEmailDialog";
import { PaymentEmailLog, PaymentEmailRow } from "../../PaymentEmailLog";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const OperatorPaymentSection = ({
  operatorPaymentData,
  currencyCode,
  operatorCurrencyCode,
  bookingId,
  paymentData,
  emails,
  emailsLoading,
}: {
  operatorPaymentData: any;
  currencyCode: string;
  operatorCurrencyCode: "EUR" | "GBP" | "USD" | "CAD" | "AUD" | "NZD";
  bookingId: string;
  paymentData: {
    bookingAmount: number;
    paidSoFar: number;
  };
  emails?: Record<string, PaymentEmailRow[]>;
  emailsLoading?: boolean;
}) => {
  const formatPrice = (price: number, toCurrency: string) => {
    const formattedNumber = new Intl.NumberFormat("en-EN", { style: "currency", currency: toCurrency }).format(price);
    return `${formattedNumber}`;
  };
  // Remittance advice per payment: editable, to the supplier or yourself.
  const [remitFor, setRemitFor] = useState<string | null>(null);
  const queryClient = useQueryClient();
  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex flex-col">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">Payments to Operator</h2>
          <AddBookingPayment
            bookingId={bookingId}
            payerType="Operator"
            paymentData={paymentData}
            bookingCurrency={operatorPaymentData?.[0]?.booking?.currency?.code || currencyCode}
            operatorCurrencyCode={operatorCurrencyCode}
          />
        </div>
        {!operatorPaymentData?.length && <p className="text-sm font-regular text-slate-700">No payments yet.</p>}
        {operatorPaymentData.map((payment: any, index: number) => (
          <div key={payment.id}>
            <div className="flex items-end ml-auto gap-.5">
              <div className="flex ml-auto">
                <EditBookingPayment
                  initData={payment}
                  bookingId={bookingId}
                  payerType="Operator"
                  paymentData={paymentData}
                  operatorCurrencyCode={operatorCurrencyCode}
                />
                <span className="flex items-center text-neutral-300">|</span>
                <RemoveBookingPayment paymentId={payment.id} bookingId={bookingId} />
                <span className="flex items-center text-neutral-300">|</span>
                <BookingPaymentLogs paymentId={payment.id} bookingId={bookingId} />
                <span className="flex items-center text-neutral-300">|</span>
                <p className="text-sm text-blue-600 font-medium cursor-pointer pl-2 pt-4 pb-4" title="Email remittance advice for this payment to the supplier (or to yourself)" onClick={() => setRemitFor(payment.id)}>Remittance</p>
              </div>
            </div>
            <div className="flex w-full">
              <p className="text-sm font-medium">Payment {operatorPaymentData.length - index}</p>
              <p className="text-sm font-medium ml-auto">{formatPrice(+payment?.amount, operatorCurrencyCode)}</p>
            </div>
            <p className="text-sm font-regular text-slate-700 mt-1">
              {formatDateUTC(payment?.paymentDate, "UTC", undefined, undefined, undefined, true)}
            </p>
            <p className="text-sm font-regular text-slate-700 mt-1">
              {payment?.paymentType === "cc_card" ? (
                <>
                  {payment?.paymentDetails?.cardCompany || "N/A"} {payment?.paymentDetails?.cardNumber || "N/A"}
                </>
              ) : payment?.paymentType === "Bank transfer" && payment?.bankAccount?.name ? (
                <>
                  <p>{payment?.paymentType}</p>
                  <p>
                    {" "}
                    {payment?.bankAccount?.name || "N/A"} • {payment?.bankAccount?.number || "N/A"}
                  </p>
                </>
              ) : (
                <>{payment?.paymentType || "N/A"}</>
              )}
            </p>
            <PaymentEmailLog rows={emails?.[payment.id]} loading={emailsLoading} />
            {index + 1 < operatorPaymentData?.length && (
              <div className="w-full h-px flex justify-end bg-blue-100 mt-4 mb-2" />
            )}
          </div>
        ))}
      </div>
      {remitFor && (
        <PaymentEmailDialog bookingId={bookingId} paymentId={remitFor} kind="remittance" onClose={() => setRemitFor(null)} onSent={() => { queryClient.invalidateQueries({ queryKey: ["paymentsData", bookingId] }); queryClient.invalidateQueries({ queryKey: ["paymentEmails", bookingId] }); }} />
      )}
    </Card>
  );
};

export default OperatorPaymentSection;
